import React, { useEffect } from 'react'
import clsx from 'clsx'
import * as Yup from 'yup'
import jwtDecode from 'jwt-decode'
import PropTypes from 'prop-types'
import { Form, Formik } from 'formik'
import { LoginSocialApple } from 'reactjs-social-login'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { makeStyles, Typography, IconButton, Link } from '@material-ui/core'

import { SECONDARY } from 'app/constants/button'
import { emailRegExpFormat } from 'commons/email'
import { disableSpaceBar } from 'commons/keyDown'
import { socialLoginScop } from 'app/constants/login'
import { privacyPolicyUrl, termsOfUseUrl } from 'commons/website'

import LabelInput from 'app/componentV4/Labels/LabelInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import PanelsIllustrationMSG from 'app/componentV4/Panels/IllustrationMSG'
import { ReactComponent as Google } from 'app/components/icons/SocialProvider/Google.svg'
import { ReactComponent as Apple } from 'app/components/icons/SocialProvider/Apple.svg'
import { ReactComponent as Facebook } from 'app/components/icons/SocialProvider/Facebook.svg'

import useLogin from './useLogin'
import { GoogleAuth } from './GoogleAuth'

const useStyles = makeStyles((theme) => ({
  root: {
    '&.MuiIconButton-root': {
      width: '50px',
      height: '50px',
      padding: '1.25rem',
      border: '1px solid',
      borderRadius: '1rem',
      borderColor: theme.colors['primary-350'],
    },
  },
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
}))

const VerificationEmailOrSocial = (props) => {
  const { onSubmit = () => {}, isLoading, onProvider = () => {} } = props
  const classes = useStyles()
  const { handleFacebookAuth } = useLogin()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleSubmit = async (values) => {
    if (!executeRecaptcha) return

    try {
      const token = await executeRecaptcha('submit')
      onSubmit({ ...values, token: token })
    } catch {
      return
    }
  }

  useEffect(() => {
    localStorage.removeItem('newAccountData')
  }, [])

  return (
    <PanelsIllustrationMSG>
      <div className="flex justify-center items-center h-full">
        <div className="w-full">
          {/* Header */}
          <Typography className="text-24 font-500 text-primary text-center">
            {'MessageSpring Admin Dashboard'}
          </Typography>

          <div className="flex items-center my-10">
            <div className="flex-1 border-t border-primary-350"></div>
            <Typography className="text-16 text-primary-300 text-center mx-5">
              {'Sign in or sign up'}
            </Typography>
            <div className="flex-1 border-t border-primary-350"></div>
          </div>

          <Typography className="text-14 text-primary-300 text-center my-10">
            {'We recommend using your work email address.'}
          </Typography>

          {/* Form Email Address */}
          <Formik
            initialValues={{
              email: '',
            }}
            validationSchema={Yup.object({
              email: Yup.string()
                .matches(emailRegExpFormat, 'Invalid email address format')
                .required('This field is required'),
            })}
            onSubmit={async (values) => {
              await handleSubmit(values)
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleBlur,
              handleChange,
            }) => (
              <Form>
                <LabelInput label={'Email Address'} required>
                  <SimpleInput
                    className={classes.input}
                    id="login-input-verify-email"
                    name="email"
                    placeholder={'Email Address'}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    inputProps={{
                      'aria-label': 'Email sign in',
                      onKeyDown: (e) => {
                        disableSpaceBar(e)
                      },
                    }}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </LabelInput>

                {/* Continue button */}
                <div className="flex justify-center mt-10">
                  <ContainedButton
                    type="submit"
                    color={SECONDARY}
                    className="w-full h-48"
                    disabled={isLoading || isSubmitting}
                    isLoading={isLoading || isSubmitting}
                    id="login-button-verify-email"
                    aria-label="Submit Verify Email"
                  >
                    {'Continue'}
                  </ContainedButton>
                </div>
              </Form>
            )}
          </Formik>

          <div className="flex items-center my-10">
            <div className="flex-1 border-t border-primary-350"></div>
            <Typography className="text-16 text-primary-300 text-center mx-5">
              {'or'}
            </Typography>
            <div className="flex-1 border-t border-primary-350"></div>
          </div>

          {/* Social sign in buttons */}
          <div className={clsx('flex justify-between max-w-256 mx-auto')}>
            <GoogleAuth
              className={classes.root}
              onResolve={(data) => onProvider(data)}
            >
              <Google />
            </GoogleAuth>

            <LoginSocialApple
              client_id={process.env.REACT_APP_APPLE_LOGIN_KEY}
              redirect_uri={process.env.REACT_APP_APPLE_REDIRECT_URI}
              scope={socialLoginScop.apple}
              onResolve={({ provider, data }) => {
                const decode = jwtDecode(data.authorization.id_token)
                onProvider({
                  provider,
                  sub: decode?.sub,
                  firstName: decode?.first_name,
                  lastName: decode?.last_name,
                  email: decode?.email,
                  profiltPicture: decode?.picture ?? '',
                  token: data.authorization.id_token,
                })
              }}
            >
              <IconButton
                id="apple-signin-button"
                className={classes.root}
                aria-label="Sign in with Apple"
              >
                <Apple />
              </IconButton>
            </LoginSocialApple>

            <IconButton
              id="facebook-signin-button"
              className={classes.root}
              onClick={async () => handleFacebookAuth()}
              aria-label="Sign in with Facebook"
            >
              <Facebook />
            </IconButton>
          </div>

          {/* Terms */}
          <Typography className="text-12 text-primary-300 text-center mt-10">
            {'By continuing, you agree to our'}
            <Link
              className="mx-2 text-primary-300 font-400 normal-case hover:bg-transparent text-nowrap border-b-1 border-dotted no-underline"
              underline="none"
              target="_blank"
              href={termsOfUseUrl}
              id="link-terms-of-service"
            >
              {'Terms of Service'}
            </Link>
            {'&'}
            <Link
              className="mx-2 text-primary-300 font-400 normal-case hover:bg-transparent text-nowrap border-b-1 border-dotted no-underline"
              underline="none"
              target="_blank"
              href={privacyPolicyUrl}
              id="link-privacy-policy"
            >
              {'Privacy Policy'}
            </Link>
          </Typography>
        </div>
      </div>
    </PanelsIllustrationMSG>
  )
}

VerificationEmailOrSocial.propTypes = {
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  onProvider: PropTypes.func,
}

export default VerificationEmailOrSocial
